import React, { useState } from "react";
import {
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Icon,
} from "@mui/material";
import AsyncSelect from "react-select/async";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import toast from "react-hot-toast";
import ServicesApi from "../../Classes/API/Services/ServicesApi";
import { daysOfWeek } from "./constants";
import DatePicker from "components/DatePicker/index";

const asyncSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: "#d2d6da",
    fontSize: "14px",
    boxShadow: "none",
    "&:hover": { borderColor: "#1961e7ff" },
  }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

export default function ServiceSelector({
  onServiceAdd,
  actionIcon = "add",
  actionColor = "dark",
}) {
  const [frequency, setFrequency] = useState("daily");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [currentService, setCurrentService] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [numberOfWeeks, setNumberOfWeeks] = useState(1);
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [customPrice, setCustomPrice] = useState("");

  const loadServiceOptions = async (v) => {
    try {
      const res = await ServicesApi.GetAllServices({
        name: v,
        pagingParams: { pageNumber: 1, pageSize: 5 },
      });
      return (res.data?.list || []).map((s) => ({
        value: s,
        label: `${s.name} (${s.price} EGP)`,
      }));
    } catch (err) {
      return [];
    }
  };

  const handleAddClick = () => {
    if (!currentService) return toast.error("Please select a service");
    if (frequency === "weekly" && selectedWeekDays.length === 0)
      return toast.error("Select at least one day");

    const totalQty =
      frequency === "daily"
        ? parseInt(quantity)
        : selectedWeekDays.length * parseInt(numberOfWeeks);
    if (totalQty <= 0) return toast.error("Quantity must be > 0");

    onServiceAdd({
      serviceId: currentService.id,
      name: currentService.name,
      price: parseFloat(customPrice || currentService.price),
      unitPrice: parseFloat(customPrice || currentService.price), // Support both naming conventions
      frequency,
      quantity: totalQty,
      startDate,
      selectedDays:
        frequency === "weekly"
          ? daysOfWeek
              .filter((d) => selectedWeekDays.includes(d.value))
              .map((d) => d.label)
          : [],
      weekDaysLabels:
        frequency === "weekly"
          ? daysOfWeek
              .filter((d) => selectedWeekDays.includes(d.value))
              .map((d) => d.label)
          : [],
      numWeeks: frequency === "weekly" ? parseInt(numberOfWeeks) : 0,
    });

    setCurrentService(null);
    setCustomPrice("");
  };

  return (
    <MDBox>
      <MDBox display="flex" gap={1} mb={2}>
        <MDButton
          variant={frequency === "daily" ? "gradient" : "outlined"}
          color="info"
          size="small"
          onClick={() => setFrequency("daily")}
        >
          Daily Mode
        </MDButton>
        <MDButton
          variant={frequency === "weekly" ? "gradient" : "outlined"}
          color="info"
          size="small"
          onClick={() => setFrequency("weekly")}
        >
          Weekly Mode
        </MDButton>
      </MDBox>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadServiceOptions}
            placeholder="Search Service..."
            isClearable
            styles={asyncSelectStyles}
            value={
              currentService
                ? { value: currentService, label: currentService.name }
                : null
            }
            onChange={(e) => {
              setCurrentService(e?.value);
              setCustomPrice(e?.value?.price || "");
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Start Date"
            selected={startDate}
            onChange={(date) => setStartDate(date)}
          />
        </Grid>
        <Grid item xs={6} md={1.5}>
          <MDInput
            type="number"
            label={frequency === "daily" ? "Days" : "Weeks"}
            value={frequency === "daily" ? quantity : numberOfWeeks}
            onChange={(e) =>
              frequency === "daily"
                ? setQuantity(e.target.value)
                : setNumberOfWeeks(e.target.value)
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={6} md={1.5}>
          <MDInput
            type="number"
            label="Price EGP"
            value={customPrice}
            onChange={(e) => setCustomPrice(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <MDButton
            variant="gradient"
            color={actionColor}
            fullWidth
            onClick={handleAddClick}
            sx={{ height: "44px" }}
          >
            <Icon>{actionIcon}</Icon>
          </MDButton>
        </Grid>

        {frequency === "weekly" && (
          <Grid item xs={12}>
            <MDBox
              mt={1}
              p={2}
              sx={{ border: "1px dashed #ced4da", borderRadius: "8px" }}
            >
              <FormGroup row>
                {daysOfWeek.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    label={
                      <MDTypography variant="button">{day.label}</MDTypography>
                    }
                    control={
                      <Checkbox
                        checked={selectedWeekDays.includes(day.value)}
                        onChange={() =>
                          setSelectedWeekDays((prev) =>
                            prev.includes(day.value)
                              ? prev.filter((d) => d !== day.value)
                              : [...prev, day.value],
                          )
                        }
                      />
                    }
                  />
                ))}
              </FormGroup>
            </MDBox>
          </Grid>
        )}
      </Grid>
    </MDBox>
  );
}
